import React from "react";
import { withStyles } from "@mui/styles";
import {
  getProfile as apiGetProfile,
  updatePlayerDetails as apiUpdatePlayerDetails,
  updateEmail as apiUpdateEmail,
  updatePassword as apiUpdatePassword,
  getPlayerDetails as apiGetPlayerDetails,
} from "../../../services/api";
import { format } from "date-fns";

import RegistrationInfoForm from "../../forms/registrationInfoForm/RegistrationInfoForm";
import EditEmailForm from "../../forms/editEmailForm/EditEmailForm";
import EditPasswordForm from "../../forms/editPasswordForm/EditPasswordForm";
import EditBankDetailsForm from "../../forms/editBankDetailsForm/EditBankDetailsForm";

import { withState } from "../../../services/state";

import PageWrapper from "../../components/pageWrapper/PageWrapper";
import DialogModal from "../../components/dialogModal/DialogModal";
import Button from "../../components/button/Button";

const styles = (theme) => ({
  root: {
    width: "100%",
    padding: "24px",
  },
  section: {
    marginBottom: "32px",
  },
  heading: {
    fontSize: "20px",
    fontWeight: 600,
    marginBottom: "8px",
  },
  paragraph: {
    marginBottom: "8px",
  },
});

function ProfilePage({ classes, dispatch, context }) {
  const [updatePlayerDetailsError, setUpdatePlayerDetailsError] =
    React.useState(null);
  const [updatingPlayerDetails, setUpdatingPlayerDetails] =
    React.useState(false);
  const [playerInformation, setPlayerInformation] = React.useState({});
  const [emailInformation, setEmailInformation] = React.useState({
    emailAddress: context?.profile.email,
  });
  const [passwordInformation, setPasswordInformation] = React.useState({});
  const [bankDetailsInformation, setBankDetailsInformation] = React.useState(
    {},
  );

  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  const [showUpdatePlayerDetails, setShowUpdatePlayerDetails] =
    React.useState(false);
  const [showUpdateEmail, setShowUpdateEmail] = React.useState(false);
  const [showUpdatePassword, setShowUpdatePassword] = React.useState(false);
  const [showUpdateBankDetails, setShowUpdateBankDetails] =
    React.useState(false);

  const [updateEmailError, setUpdateEmailError] = React.useState(null);
  const [updatingEmail, setUpdatingEmail] = React.useState(false);

  const [updatePasswordError, setUpdatePasswordError] = React.useState(null);
  const [updatingPassword, setUpdatingPassword] = React.useState(false);

  const homeAddress =
    context.currentPlayer?.playerInformation?.homeAddress || "Not set";

  const isRequired = (field) =>
    context.currentClub.playerInformationSettings?.[field].toString() === "1" ||
    context.currentClub.playerInformationSettings?.[field].toString() === "2";

  const isInformationComplete = (requiredFields, playerDetails) => {
    if (requiredFields) {
      for (let field in requiredFields) {
        if (
          requiredFields[field] === 2 &&
          (!playerDetails ||
            !playerDetails[field] ||
            playerDetails[field].length === 0)
        ) {
          return false;
        }
      }
    }
    return true;
  };

  const updatePlayerDetails = async () => {
    setUpdatePlayerDetailsError(null);
    setSubmitAttempted(false);

    if (
      isInformationComplete(
        context.currentClub.playerInformationSettings,
        playerInformation,
      )
    ) {
      if (
        playerInformation.agree ||
        !context.currentClub.latestPrivacyPolicy?.privacyPolicy ||
        context.currentClub.latestPrivacyPolicy?.privacyPolicy.length === 0
      ) {
        setUpdatingPlayerDetails(true);
        try {
          await apiUpdatePlayerDetails(context.currentPlayer.id, {
            firstName: context.currentPlayer.firstName,
            lastName: context.currentPlayer.lastName,
            ...context.currentPlayer.playerInformation,
            ...playerInformation,
          });
          const getProfileData = await apiGetProfile();
          dispatch({
            type: "storeProfile",
            profile: getProfileData,
          });

          const getPlayerDetails = await apiGetPlayerDetails(
            context.currentPlayer.id,
          );
          dispatch({
            type: "storeCurrentPlayer",
            currentPlayer: getPlayerDetails,
          });

          setShowUpdatePlayerDetails(false);
        } catch (e) {
          setUpdatePlayerDetailsError(
            "An error occurred updating player details",
          );
        } finally {
          setUpdatingPlayerDetails(false);
        }
      } else {
        setUpdatePlayerDetailsError(
          "Please agree to the club's privacy policy",
        );
        setUpdatingPlayerDetails(false);
      }
    } else {
      setUpdatePlayerDetailsError("Please complete all required fields");
      setUpdatingPlayerDetails(false);
    }
  };

  const updateEmail = async () => {
    setUpdateEmailError(null);
    setSubmitAttempted(false);
    setUpdatingEmail(true);
    try {
      await apiUpdateEmail({
        emailAddress: emailInformation.emailAddress,
      });

      setShowUpdateEmail(false);
    } catch (e) {
      setUpdateEmailError("An error occurred updating player details");
    } finally {
      setUpdatingEmail(false);
    }
  };

  const updatePassword = async () => {
    setUpdateEmailError(null);
    setSubmitAttempted(false);
    setUpdatingPassword(true);
    try {
      if (
        passwordInformation.newPassword !== passwordInformation.repeatPassword
      ) {
        return setUpdatePasswordError("Passwords do not match");
      }

      await apiUpdatePassword({
        oldPassword: passwordInformation.oldPassword,
        newPassword: passwordInformation.newPassword,
      });

      setShowUpdatePassword(false);
    } catch (e) {
      setUpdatePasswordError("An error occurred updating player details");
    } finally {
      setUpdatingPassword(false);
    }
  };

  return (
    <PageWrapper showHeader={true} showSidebar={true} title="Your Profile">
      <div className={classes.root}>
        <div className={classes.section}>
          <div className={classes.heading}>Email</div>
          <div className={classes.paragraph}>{context.profile.email}</div>
          <Button
            secondary
            onClick={() => setShowUpdateEmail(true)}
            title="Edit Email"
          />
        </div>

        <div className={classes.section}>
          <div className={classes.heading}>Password</div>
          <div className={classes.paragraph}>••••••••</div>
          <Button
            secondary
            onClick={() => setShowUpdatePassword(true)}
            title="Change Password"
          />
        </div>

        <div className={classes.section}>
          <div className={classes.heading}>Player Information</div>
          <div className={classes.paragraph}>
            First Name: {context.currentPlayer.firstName}
          </div>
          <div className={classes.paragraph}>
            Last Name: {context.currentPlayer.lastName}
          </div>
          {context.currentPlayer.playerInformation?.gender &&
            isRequired("gender") && (
              <div className={classes.paragraph}>
                Gender: {context.currentPlayer.playerInformation?.gender}
              </div>
            )}
          {context.currentPlayer.playerInformation?.dateOfBirth &&
            isRequired("dateOfBirth") && (
              <div className={classes.paragraph}>
                Date of Birth:{" "}
                {format(
                  context.currentPlayer.playerInformation?.dateOfBirth,
                  "MMMM do yyyy",
                )}
              </div>
            )}
          {context.currentPlayer.playerInformation?.phoneNumber &&
            isRequired("phoneNumber") && (
              <div className={classes.paragraph}>
                Phone Number:{" "}
                {context.currentPlayer.playerInformation?.phoneNumber}
              </div>
            )}
          {homeAddress && isRequired("homeAddress") && (
            <div className={classes.paragraph}>Address: {homeAddress}</div>
          )}
          {context.currentPlayer.playerInformation?.medicalConditions &&
            isRequired("medicalConditions") && (
              <div className={classes.paragraph}>
                Medical Conditions:{" "}
                {context.currentPlayer.playerInformation?.medicalConditions}
              </div>
            )}
          {context.currentPlayer.playerInformation?.emergencyContactName &&
            isRequired("emergencyContactName") && (
              <div className={classes.paragraph}>
                Emergency Contact Name:{" "}
                {context.currentPlayer.playerInformation?.emergencyContactName}
              </div>
            )}
          {context.currentPlayer.playerInformation?.emergencyContactNumber &&
            isRequired("emergencyContactNumber") && (
              <div className={classes.paragraph}>
                Emergency Contact Number:{" "}
                {
                  context.currentPlayer.playerInformation
                    ?.emergencyContactNumber
                }
              </div>
            )}
          {/* Additional fields can go here if needed */}
          <Button
            secondary
            onClick={() => setShowUpdatePlayerDetails(true)}
            title="Edit Player Info"
          />
        </div>

        <div className={classes.section}>
          <div className={classes.heading}>Payment</div>
          <Button
            secondary
            onClick={() => setShowUpdateBankDetails(true)}
            title="Manage Bank Details"
          />
        </div>
      </div>

      {/* Update Player Details Modal */}
      {showUpdatePlayerDetails && (
        <DialogModal
          title="Update Profile"
          onSubmit={updatePlayerDetails}
          onClose={() => {
            setSubmitAttempted(false);
            setShowUpdatePlayerDetails(false);
          }}
          submitText="Save"
          submitting={updatingPlayerDetails}
        >
          <RegistrationInfoForm
            requiredFields={context.currentClub.playerInformationSettings}
            latestPrivacyPolicy={context.currentClub.latestPrivacyPolicy}
            payload={{
              firstName: context.currentPlayer.firstName,
              lastName: context.currentPlayer.lastName,
              ...context.currentPlayer.playerInformation,
              ...playerInformation,
            }}
            storePayload={setPlayerInformation}
            errorMessage={updatePlayerDetailsError}
            submitAttempted={submitAttempted}
          />
        </DialogModal>
      )}

      {/* Update Email Modal */}
      {showUpdateEmail && (
        <DialogModal
          title="Update Email"
          onSubmit={updateEmail}
          onClose={() => {
            setSubmitAttempted(false);
            setShowUpdateEmail(false);
          }}
          submitText="Save"
          submitting={updatingEmail}
        >
          <EditEmailForm
            requiredFields={context.currentClub.playerInformationSettings}
            latestPrivacyPolicy={context.currentClub.latestPrivacyPolicy}
            payload={emailInformation}
            storePayload={setEmailInformation}
            errorMessage={updateEmailError}
            submitAttempted={submitAttempted}
          />
        </DialogModal>
      )}

      {/* Update Password Modal */}
      {showUpdatePassword && (
        <DialogModal
          title="Update Password"
          onSubmit={updatePassword}
          onClose={() => {
            setSubmitAttempted(false);
            setShowUpdatePassword(false);
          }}
          submitText="Save"
          submitting={updatingPassword}
        >
          <EditPasswordForm
            requiredFields={context.currentClub.playerInformationSettings}
            latestPrivacyPolicy={context.currentClub.latestPrivacyPolicy}
            payload={passwordInformation}
            storePayload={setPasswordInformation}
            errorMessage={updatePasswordError}
            submitAttempted={submitAttempted}
          />
        </DialogModal>
      )}

      {showUpdateBankDetails && (
        <DialogModal
          title="Update Bank Details"
          onSubmit={() => {}}
          onClose={() => {
            setSubmitAttempted(false);
            setShowUpdateBankDetails(false);
          }}
          submitText="Done"
        >
          <EditBankDetailsForm
            requiredFields={context.currentClub.playerInformationSettings}
            latestPrivacyPolicy={context.currentClub.latestPrivacyPolicy}
            payload={bankDetailsInformation}
            storePayload={setBankDetailsInformation}
            submitAttempted={submitAttempted}
          />
        </DialogModal>
      )}
    </PageWrapper>
  );
}

export default withStyles(styles)(withState(ProfilePage));
